/*
 * <license header>
 */

import React, { useEffect, useState } from "react";
import {
  Provider,
  defaultTheme,
  View,
  Divider,
  Heading,
  Text,
} from "@adobe/react-spectrum";
import ErrorBoundary from "react-error-boundary";
import { HashRouter as Router, Switch, Route } from "react-router-dom";
import { Form } from "./Form";
import { OktaAuth, toRelativeUrl } from "@okta/okta-auth-js";
import { useHistory } from "react-router-dom";
import { Banner } from "./Banner";
import "./Form.css";

const oktaIssuer = process.env.REACT_APP_OKTA_ISSUER;
const oktaClientId = process.env.REACT_APP_OKTA_CLIENT_ID;

function App(props) {
  //console.log("runtime object:", props.runtime);
  //console.log("ims object:", props.ims);

  /** Begin OKTA support **/
  const [authenticated, setAuthenticated] = useState(false);
  const history = useHistory();

  const oktaAuth = new OktaAuth({
    issuer: oktaIssuer,
    clientId: oktaClientId,
    redirectUri: window.location.origin,
  });

  useEffect(() => {
    oktaAuth.isAuthenticated().then((isAuthenticated) => {
      if (!isAuthenticated) {
        // Redirect to Okta for authentication
        oktaAuth.signInWithRedirect();
      } else {
        setAuthenticated(true);
      }
    });
  }, [history]);

  // Handle the callback from Okta
  if (window.location.search?.includes("code=")) {
    oktaAuth.handleLoginRedirect().then(() => {
      const fromOkta = toRelativeUrl(
        window.location.href,
        window.location.origin
      );
      if (history) {
        history.replace(fromOkta);
      }
    });
  }

  if (!authenticated) {
    return (<></>);
  }

  /** End OKTA support */

  // use exc runtime event handlers
  // respond to configuration change events (e.g. user switches org)
  props.runtime.on("configuration", ({ imsOrg, imsToken, locale }) => {
    console.log("configuration change", { imsOrg, imsToken, locale });
  });
  // respond to history change events
  props.runtime.on("history", ({ type, path }) => {
    console.log("history change", { type, path });
  });

  return (
    <ErrorBoundary onError={onError} FallbackComponent={fallbackComponent}>
      <Router>
        <Provider theme={defaultTheme} colorScheme={"light"}>
          <Banner></Banner>

          <View
            alignSelf={"start"}
            maxWidth="800px"
            marginStart={"auto"}
            marginEnd={"auto"}
            marginBottom={"size-1200"}
          >
            <br />
            
            <Heading level={1}>
              Experience League Event Advertisement Submission
            </Heading>
  
            <p>
              Use this form to advertise a customer facing event on Experience League.  The event will appear on the <a href="https://experienceleague.adobe.com/events" target="_blank" rel="referrer">Experience League events page</a> as well as on pages for the specified products.
            </p>
    
            <p>
            For more specifics about the Event posting requirements, view&nbsp;
            <a href="https://experienceleague.corp.adobe.com/docs/authoring-guide-exl/using/getting-started/quick-start-guides/quick-start-events/quick-start-advertise-event.html?lang=en" target="_blank" rel="referrer">this page</a>&nbsp;(VPN required).  Events should be,            
            </p>

            <ul>
              <li>Adobe run</li>
              <li>Customer facing</li>
              <li>Not a marketing or sales event (use <a href="https://business.adobe.com/" target="_blank" rel="referrer">b.a.com</a> for those types of events)</li>
            </ul>

            <p>
              Event will be posted within 24 hours of submission.
            </p>

            <p>
              Please email <a href="mailto:Grp-ExLevents@adobe.com">Grp-ExLevents@adobe.com</a> with any questions.
            </p>

            <Divider size="S" marginBottom="size-200" marginTop="size-200" />

            <Switch>
              <Route exact path="/">
                <Form ims={props.ims} oktaAuth={oktaAuth}></Form>
              </Route>
            </Switch>

            <Divider
              size="S"
              marginTop={"size-200"}
              marginBottom={"size-200"}
            />
          </View>
        </Provider>
      </Router>
    </ErrorBoundary>
  );

  // Methods

  // error handler on UI rendering failure
  function onError(e, componentStack) {}

  // component to show if UI fails rendering
  function fallbackComponent({ componentStack, error }) {
    return (
      <React.Fragment>
        <h1 className="error-fallback-heading">
          Something went wrong :(
        </h1>
        <pre>{componentStack + "\n" + error.message}</pre>
      </React.Fragment>
    );
  }
}

export default App;
